import { forwardRef } from 'react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Slot, Slottable } from '@radix-ui/react-slot'
import { cn } from '@/lib/cn'
import { ICON_SIZE, ICON_STROKE, Loader2, type LucideIcon } from '@/components/icons'

/** The only button in the product. */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Disables the button and swaps the icon for a spinner. */
  loading?: boolean
  icon?: LucideIcon
  iconPosition?: 'left' | 'right'
  fullWidth?: boolean
  /** Render as the child element (a Link, say) while keeping button styling. */
  asChild?: boolean
  children?: ReactNode
}

const VARIANTS: Record<ButtonVariant, string> = {
  // Solid fill. Not a gradient — see tokens.css.
  primary:
    'bg-primary-strong text-on-primary hover:bg-primary-hover active:bg-primary-active ' +
    'disabled:bg-primary-strong/40',
  secondary:
    'bg-surface text-text border border-line hover:bg-hover ' +
    'active:bg-neutral-bg disabled:text-subtle',
  ghost:
    'bg-transparent text-muted hover:bg-hover hover:text-text ' +
    'active:bg-neutral-bg disabled:text-subtle',
  danger:
    'bg-danger-bg text-danger border border-danger-border hover:bg-danger/10 ' +
    'active:bg-danger/15 disabled:opacity-50',
}

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-9 px-4 text-base gap-2',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    icon: Icon,
    iconPosition = 'left',
    fullWidth = false,
    asChild = false,
    className,
    disabled,
    children,
    type = 'button',
    ...props
  },
  ref,
) {
  const Comp = asChild ? Slot : 'button'
  const isDisabled = disabled === true || loading
  const iconSize = size === 'sm' ? ICON_SIZE.xs : ICON_SIZE.sm

  // While loading the spinner takes the icon's slot rather than being appended,
  const leading =
    loading ? <Loader2 size={iconSize} strokeWidth={ICON_STROKE} className="animate-spin" aria-hidden />
    : Icon && iconPosition === 'left' ? <Icon size={iconSize} strokeWidth={ICON_STROKE} aria-hidden />
    : null

  const trailing =
    !loading && Icon && iconPosition === 'right'
      ? <Icon size={iconSize} strokeWidth={ICON_STROKE} aria-hidden />
      : null

  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : type}
      disabled={asChild ? undefined : isDisabled}
      // Communicates the in-flight state to assistive tech, which `disabled`
      aria-busy={loading || undefined}
      data-loading={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center rounded-md font-medium',
        'transition-colors duration-150',
        'disabled:cursor-not-allowed',
        // Never outline:none with nothing to replace it.
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
        'whitespace-nowrap select-none',
        SIZES[size],
        VARIANTS[variant],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {leading}
      {/*
        Slottable wraps button content safely for Radix Slot component.
      */}
      <Slottable>{children}</Slottable>
      {trailing}
    </Comp>
  )
})
